/**
 * An item's link groups in its tree (Tabler has neither as drawn): its links out, Tabler's `share` turned until two of its
 * nodes are level (so it isn't Android's share), and the items linking to it, `folder-symlink`'s arrow into a node the size of share's.
 */

import type { ComponentChildren } from "preact";
import { useId } from "../lib/uid.ts";

/** As the other icons (outlined: Tabler's size and stroke). */
const SIZE = 16;
const STROKE = 1.75;

function Glyph({ size, cls, children }: { size: number; cls: string | undefined; children: ComponentChildren }) {
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      stroke-width={STROKE}
      stroke-linecap="round"
      stroke-linejoin="round"
      aria-hidden="true"
      class={`shrink-0 ${cls ?? ""}`}
    >
      {children}
    </svg>
  );
}

/** Links out of an item: one node branching to others. */
export function LinksIcon({ class: cls, size = SIZE }: { class?: string; size?: number }) {
  return (
    <Glyph size={size} cls={cls}>
      {/* atan(½): its node and the upper one it links to level (6 down over 12 across). */}
      <g transform="rotate(26.565 12 12)">
        <path d="M3 12a3 3 0 1 0 6 0a3 3 0 1 0 -6 0" />
        <path d="M15 6a3 3 0 1 0 6 0a3 3 0 1 0 -6 0" />
        <path d="M15 18a3 3 0 1 0 6 0a3 3 0 1 0 -6 0" />
        <path d="M8.7 10.7l6.6 -3.4" />
        <path d="M8.7 13.3l6.6 3.4" />
      </g>
    </Glyph>
  );
}

/** Items linking to one: an arrow coming into a node. */
export function LinkedFromIcon({ class: cls, size = SIZE }: { class?: string; size?: number }) {
  return (
    <Glyph size={size} cls={cls}>
      {/* Coming up from below, as what links here does when the group opens under it. */}
      <g transform="translate(0 -3)">
        <path d="M3 21v-4a3 3 0 0 1 3 -3h5" />
        <path d="M8 17l3 -3l-3 -3" />
        <path d="M14 14a3 3 0 1 0 6 0a3 3 0 1 0 -6 0" />
      </g>
    </Glyph>
  );
}

/** A link icon's line runs across, between these. */
const LINE_FROM = 2;
const LINE_TO = 22;
/** Half its width: where there's one (a stroke's), where there can be many, and many at both ends (as the map's). */
const ONE_HALF = 0.875;
const MANY_HALF = 3.25;
const MUTUAL_HALF = 1.75;
/** An ownership's diamond: its length along the line, half its width across, and how far in from its end it sits (the
 * line going on past it, so it reads as a diamond on the line, not its arrowhead). */
const DIAMOND_LONG = 12;
const DIAMOND_HALF = 3.75;
const DIAMOND_IN = 2;
/** The gap cut round it in the line under it (the map's white outline, on any background). */
const DIAMOND_GAP = 2.5;

/**
 * A link as the simple map draws one (its key's), as an icon: from what's before it (left) to what's after, wide at an
 * end where there can be many and thin where there's one (`many`), a diamond over it by the whole's end of an
 * ownership (`whole`). Solid, as the map's lines are.
 */
export function LinkLine({
  many,
  whole = null,
  size = SIZE,
}: {
  many: readonly [boolean, boolean];
  whole?: "start" | "end" | null;
  size?: number;
}) {
  const [h0, h1] =
    many[0] && many[1] ? [MUTUAL_HALF, MUTUAL_HALF] : [many[0] ? MANY_HALF : ONE_HALF, many[1] ? MANY_HALF : ONE_HALF];
  const id = useId();
  const at = whole === "start" ? LINE_FROM + DIAMOND_IN : LINE_TO - DIAMOND_IN - DIAMOND_LONG;
  const diamond = `M${at} 12l${DIAMOND_LONG / 2} ${-DIAMOND_HALF}l${DIAMOND_LONG / 2} ${DIAMOND_HALF}l${-DIAMOND_LONG / 2} ${DIAMOND_HALF}Z`;
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" aria-hidden="true" class="shrink-0 fill-current">
      {whole ? (
        <mask id={id}>
          <rect width="24" height="24" fill="white" />
          <path d={diamond} fill="black" stroke="black" stroke-width={DIAMOND_GAP} stroke-linejoin="round" />
        </mask>
      ) : null}
      <path
        d={`M${LINE_FROM} ${12 - h0}L${LINE_TO} ${12 - h1}L${LINE_TO} ${12 + h1}L${LINE_FROM} ${12 + h0}Z`}
        mask={whole ? `url(#${id})` : undefined}
      />
      {whole ? <path d={diamond} /> : null}
    </svg>
  );
}
